import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, CloudUpload, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STATS = [
  ["pending", "Pending", "text-foreground/70"],
  ["processing", "Processing", "text-gold"],
  ["ready", "Ready", "text-emerald-400"],
  ["failed", "Failed", "text-red-400"],
];

const sum = (c, key) => (c?.video?.[key] || 0) + (c?.trailer?.[key] || 0);

export const BunnyMigrationCard = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/bunny/migration");
      setData(d);
    } catch {
      /* card stays hidden */
    }
  }, []);
  const running = data?.job?.status === "running";

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { load(); onChanged?.(); }, 5000);
    return () => clearInterval(t);
  }, [running, load, onChanged]);

  const start = async (onlyFailed) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/admin/bunny/migrate-all", { only_failed: onlyFailed });
      toast.success(d.message);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start migration");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const c = data.counts;
  const total = sum(c, "total");
  const pct = total ? Math.round((sum(c, "ready") / total) * 100) : 0;
  const failed = sum(c, "failed");
  const todo = sum(c, "pending") + failed;

  return (
    <div className="mb-6 rounded-xl border border-white/10 bg-surface p-5" data-testid="bunny-migration-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold">Bunny Stream migration</p>
          <p className="text-sm text-foreground/60 mt-1 max-w-xl">
            Copies every film and trailer to Bunny. Films keep playing from current storage until Bunny finishes encoding; originals are kept as a backup.
          </p>
          <p className="text-xs text-foreground/50 mt-2" data-testid="bunny-migration-job-status">
            {running ? `Running · ${data.job.phase}` : data.job?.status ? `Last run: ${data.job.status}` : "Not run yet"}
            {data.job?.errors ? ` · ${data.job.errors} error(s)` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => start(false)} disabled={busy || running || !data.enabled || todo === 0} className="bg-gold text-black hover:bg-gold/90" data-testid="bunny-migrate-all-btn">
            {busy || running ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />} Migrate all films
          </Button>
          {failed > 0 && (
            <Button variant="outline" onClick={() => start(true)} disabled={busy || running} className="border-white/15" data-testid="bunny-retry-failed-btn">
              <RotateCcw className="w-4 h-4" /> Retry failed
            </Button>
          )}
        </div>
      </div>
      <Progress value={pct} className="h-1.5 mt-4" data-testid="bunny-migration-progress" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        {STATS.map(([key, label, tone]) => (
          <div key={key} className="rounded-lg bg-white/[0.03] px-3 py-2" data-testid={`bunny-migration-${key}`}>
            <p className={`text-xl font-semibold tabular-nums ${tone}`}>{sum(c, key)}</p>
            <p className="text-[11px] text-foreground/50">{label} · films {c.video[key]} / trailers {c.trailer[key]}</p>
          </div>
        ))}
      </div>
      {!data.enabled && <p className="text-xs text-red-400 mt-3">Bunny Stream isn't configured.</p>}
    </div>
  );
};
